import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Pencil, Loader2, Sparkles } from "lucide-react";


export default function SeoPagesPage() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editPage, setEditPage] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchPages = async () => {
    try {
      const res = await api.get(`/pages-seo`);
      setPages(res.data);
    } catch (err) {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPages(); }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/pages-seo/generate`);
      toast.success(`${res.data.total} pages SEO generees`);
      fetchPages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur generation");
    } finally {
      setGenerating(false);
    }
  };

  const handleEdit = (page) => {
    setEditPage(page);
    setEditData({ ...page });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/pages-seo/${editPage.id}`, editData);
      toast.success("Page mise a jour");
      setEditPage(null);
      fetchPages();
    } catch (err) {
      toast.error("Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const updateCopy = (field, value) =>
    setEditData((p) => ({ ...p, copywriting: { ...p.copywriting, [field]: value } }));

  return (
    <div className="space-y-6" data-testid="seo-pages-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">Pages SEO</h1>
          <p className="text-sm text-slate-500 mt-1">{pages.length} page(s) generee(s)</p>
        </div>
        <Button
          onClick={handleGenerate}
          disabled={generating}
          className="gap-2 bg-[#336DBF] hover:bg-[#2A5A9E]"
          data-testid="generate-seo-button"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Generer les pages
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#336DBF]" />
            </div>
          ) : pages.length === 0 ? (
            <p className="text-sm text-slate-400 py-12 text-center">
              Aucune page SEO. Cliquez sur "Generer les pages" pour creer les pages service x ville.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Slug</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Service</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Ville</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Intention</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Actif</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pages.map((p) => (
                  <TableRow key={p.id} className="hover:bg-slate-50/50">
                    <TableCell className="text-sm font-medium text-slate-900 font-mono text-xs">{p.slug}</TableCell>
                    <TableCell className="text-sm text-slate-600">{p.service_name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{p.ville_name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="bg-slate-100 text-slate-700">{p.niveau_intention}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className={`w-2.5 h-2.5 rounded-full ${p.is_active ? "bg-emerald-500" : "bg-slate-300"}`} />
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(p)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editPage} onOpenChange={() => setEditPage(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh]" data-testid="edit-seo-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifier la page SEO</DialogTitle>
            <DialogDescription>{editData.slug}</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Meta title</label>
                <Input value={editData.meta_title || ""} onChange={(e) => setEditData((p) => ({ ...p, meta_title: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Meta description</label>
                <Textarea value={editData.meta_description || ""} onChange={(e) => setEditData((p) => ({ ...p, meta_description: e.target.value }))} rows={2} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Zone detail</label>
                <Textarea value={editData.zone_detail || ""} onChange={(e) => setEditData((p) => ({ ...p, zone_detail: e.target.value }))} rows={2} />
              </div>
              {editData.copywriting && Object.keys(editData.copywriting).map((key) => (
                <div key={key}>
                  <label className="text-sm font-medium text-slate-700 mb-1 block capitalize">{key.replace(/_/g, " ")}</label>
                  <Textarea
                    value={editData.copywriting[key] || ""}
                    onChange={(e) => updateCopy(key, e.target.value)}
                    rows={3}
                  />
                </div>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPage(null)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#336DBF] hover:bg-[#2A5A9E]" data-testid="save-seo-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
